import React, { FC, useEffect, useRef, useState } from 'react'

import { motion } from 'motion/react'
import { useTranslation } from 'react-i18next'

import { TourNav } from '../FullPageOnboarding/components/TourNav/TourNav'
import { VerticalSlide } from '../FullPageOnboarding/components/VerticalSlide'
import styles from './FullTour.module.scss'
import {
  FullTourProps,
  TourPageDefinition,
  TourPageKey,
  TourPageNavigation,
  TourPageRenderer,
  TourPageShell
} from './FullTour.types'
import { FullTourIntro } from './FullTourIntro'
import { FullTourStep } from './FullTourStep'
import { FullTourStepEight } from './FullTourStepEight'
import { FullTourStepFive } from './FullTourStepFive'
import { FullTourStepFour } from './FullTourStepFour'
import { FullTourStepSeven } from './FullTourStepSeven'
import { FullTourStepSix } from './FullTourStepSix'
import { FullTourStepThree } from './FullTourStepThree'
import { FullTourStepTwo } from './FullTourStepTwo'

const TOUR_TRANSITION_DURATION = 0.38

const renderIntro: TourPageRenderer = ({ onBegin, onSkip }) => (
  <FullTourIntro onSkip={onSkip} onStart={onBegin} />
)

const renderStepOne: TourPageRenderer = () => <FullTourStep />

const renderStepTwo: TourPageRenderer = () => <FullTourStepTwo />

const renderStepThree: TourPageRenderer = () => <FullTourStepThree />
const renderStepFour: TourPageRenderer = () => <FullTourStepFour />
const renderStepFive: TourPageRenderer = () => <FullTourStepFive />
const renderStepSix: TourPageRenderer = () => <FullTourStepSix />
const renderStepSeven: TourPageRenderer = () => <FullTourStepSeven />
const renderStepEight: TourPageRenderer = () => <FullTourStepEight />

const TOUR_PAGES = [
  {
    key: 'intro',
    layout: 'intro',
    nextKey: 'step-one',
    progressIndex: null,
    renderer: renderIntro,
    shell: 'intro'
  },
  {
    key: 'step-one',
    layout: 'step',
    nextKey: 'step-two',
    previousKey: 'intro',
    progressIndex: 0,
    renderer: renderStepOne,
    shell: 'step'
  },
  {
    key: 'step-two',
    layout: 'step',
    nextKey: 'step-three',
    previousKey: 'step-one',
    progressIndex: 1,
    renderer: renderStepTwo,
    shell: 'step'
  },
  {
    key: 'step-three',
    layout: 'step',
    nextKey: 'step-four',
    previousKey: 'step-two',
    progressIndex: 2,
    renderer: renderStepThree,
    shell: 'step'
  },
  {
    key: 'step-four',
    layout: 'step',
    nextKey: 'step-five',
    previousKey: 'step-three',
    progressIndex: 3,
    renderer: renderStepFour,
    shell: 'projects'
  },
  {
    key: 'step-five',
    layout: 'step',
    nextKey: 'step-six',
    previousKey: 'step-four',
    progressIndex: 4,
    renderer: renderStepFive,
    shell: 'timesheets'
  },
  {
    key: 'step-six',
    layout: 'step',
    nextKey: 'step-seven',
    previousKey: 'step-five',
    progressIndex: 5,
    renderer: renderStepSix,
    shell: 'monthly-signoff'
  },
  {
    key: 'step-seven',
    layout: 'step',
    nextKey: 'step-eight',
    previousKey: 'step-six',
    progressIndex: 6,
    renderer: renderStepSeven,
    shell: 'user-management'
  },
  {
    key: 'step-eight',
    layout: 'step',
    previousKey: 'step-seven',
    progressIndex: 7,
    renderer: renderStepEight,
    shell: 'funding-potential'
  }
] satisfies readonly TourPageDefinition[]

const CARD_SHELL_CLASSES: Record<TourPageShell, string> = {
  intro: styles.introCardShell,
  step: styles.stepCardShell,
  projects: `${styles.stepCardShell} ${styles.projectsStepCardShell}`,
  timesheets: `${styles.stepCardShell} ${styles.timesheetsStepCardShell}`,
  'monthly-signoff': `${styles.stepCardShell} ${styles.monthlySignoffStepCardShell}`,
  'user-management': `${styles.stepCardShell} ${styles.userManagementStepCardShell}`,
  'funding-potential': `${styles.stepCardShell} ${styles.fundingPotentialStepCardShell}`
}

const SPLIT_LAYOUT_CLASSES: Record<Exclude<TourPageShell, 'intro'>, string> = {
  step: styles.splitLayout,
  projects: `${styles.splitLayout} ${styles.projectsSplitLayout}`,
  timesheets: `${styles.splitLayout} ${styles.timesheetsSplitLayout}`,
  'monthly-signoff': `${styles.splitLayout} ${styles.monthlySignoffSplitLayout}`,
  'user-management': `${styles.splitLayout} ${styles.userManagementSplitLayout}`,
  'funding-potential': `${styles.splitLayout} ${styles.fundingPotentialSplitLayout}`
}

export const FullTour: FC<FullTourProps> = ({ onComplete, onSkip }) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'desktop.full_tour'
  })
  const [currentPageKey, setCurrentPageKey] = useState<TourPageKey>('intro')
  const [direction, setDirection] = useState(1)
  const steps = t('steps', { returnObjects: true }) as string[]
  const currentPageIndex = TOUR_PAGES.findIndex(
    page => page.key === currentPageKey
  )
  const currentPage = TOUR_PAGES[currentPageIndex]
  const isTourStarted = currentPage.layout === 'step'
  const nextButtonRef = useRef<HTMLButtonElement>(null)

  const advanceToPage = (pageKey: TourPageKey) => {
    const nextIndex = TOUR_PAGES.findIndex(page => page.key === pageKey)

    if (nextIndex !== -1) {
      setDirection(nextIndex > currentPageIndex ? 1 : -1)
      setCurrentPageKey(pageKey)
    }
  }

  useEffect(() => {
    if (isTourStarted) {
      nextButtonRef.current?.focus()
    }
  }, [currentPageKey, isTourStarted])

  const handleBack = () => {
    if (currentPage.previousKey) {
      advanceToPage(currentPage.previousKey)
    }
  }
  const handleNext = () => {
    if (currentPage.nextKey) {
      advanceToPage(currentPage.nextKey)
      return
    }
    onComplete()
  }

  const pageNavigation: TourPageNavigation = {
    onBack: handleBack,
    onBegin: () => advanceToPage('step-one'),
    onNext: handleNext,
    onSkip
  }

  return (
    <div className={styles.fullTour}>
      <motion.div
        layout
        className={`${styles.cardShell} ${CARD_SHELL_CLASSES[currentPage.shell]}`}
        transition={{
          layout: {
            duration: TOUR_TRANSITION_DURATION,
            ease: 'easeInOut'
          }
        }}
      >
        {isTourStarted ? (
          <div className={SPLIT_LAYOUT_CLASSES[currentPage.shell]}>
            <div className={styles.slide}>
              <VerticalSlide
                animateOnMount={false}
                direction={direction}
                duration={TOUR_TRANSITION_DURATION}
                slideKey={currentPage.key}
              >
                {currentPage.renderer(pageNavigation)}
              </VerticalSlide>
            </div>
            <div className={styles.navigationWrapper}>
              <TourNav
                activeIndex={currentPage.progressIndex ?? 0}
                ariaLabel={t('navigation.progress_label')}
                backLabel={t('navigation.back')}
                nextLabel={
                  currentPage.key === 'step-eight'
                    ? t('navigation.finish')
                    : t('navigation.next')
                }
                nextButtonRef={nextButtonRef}
                onBack={handleBack}
                onNext={handleNext}
                showBack
                skipLabel={t('navigation.skip')}
                total={steps.length}
                onSkip={onSkip}
              />
            </div>
          </div>
        ) : (
          <VerticalSlide
            animateOnMount={false}
            className={styles.page}
            direction={direction}
            duration={TOUR_TRANSITION_DURATION}
            slideKey={currentPage.key}
          >
            {currentPage.renderer(pageNavigation)}
          </VerticalSlide>
        )}
      </motion.div>
    </div>
  )
}
